import * as THREE from 'three';
import { CREATIVE_WORLD } from './CreativeBiome';
import { legacyThemeEnvironment } from './LegacyThemeCatalog';
export { creativeBiomeWeights } from './CreativeBiome';

// Summer is the base map; the remaining themes follow CreativeWorld.regions.
export const CREATIVE_BIOME_TEXTURES = CREATIVE_WORLD.regions
  .slice(1)
  .map(({ theme }) => legacyThemeEnvironment(theme).terrainTexture);

// One packed atlas keeps fourteen landscapes within the WebGL sampler budget.
function biomeAtlas(textures: readonly THREE.Texture[]): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  const size = 512;
  canvas.width = canvas.height = size * 4;
  const context = canvas.getContext('2d');
  if (context === null) throw new Error('Cannot create landscape atlas');
  textures.forEach((texture, i) => {
    if (texture.image)
      context.drawImage(
        texture.image as CanvasImageSource,
        (i % 4) * size,
        Math.floor(i / 4) * size,
        size,
        size,
      );
  });
  const atlas = new THREE.CanvasTexture(canvas);
  atlas.colorSpace = THREE.NoColorSpace;
  atlas.wrapS = atlas.wrapT = THREE.ClampToEdgeWrapping;
  return atlas;
}

export function configureCreativeTerrainMaterial(
  material: THREE.MeshLambertMaterial | THREE.MeshPhongMaterial,
  textures: readonly THREE.Texture[],
): void {
  const atlas = biomeAtlas(textures);
  material.addEventListener('dispose', () => atlas.dispose());
  const configureLegacy = material.onBeforeCompile.bind(material);
  const legacyKey = material.customProgramCacheKey();
  material.onBeforeCompile = (shader, renderer) => {
    configureLegacy(shader, renderer);
    // Keep the normal terrain tint through the wave-exposed bank. The legacy
    // height clamp replaces the texture with a solid bright color below zero,
    // creating a white strip whenever a wave recedes. Fade only into the deep seabed.
    shader.fragmentShader = shader.fragmentShader.replace(
      'clamp(vLegacyTerrainHeight, 0.0, 0.9)',
      '0.9 + 0.1 * (1.0 - smoothstep(-20.0, -4.0, vLegacyTerrainHeight))',
    );
    shader.uniforms.creativeAtlas = { value: atlas };
    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        '#include <common>\nattribute vec4 biomeExtraA;\nattribute vec4 biomeExtraB;\nvarying vec4 vBiomeExtraA;\nvarying vec4 vBiomeExtraB;\nattribute float biomeWinterBeachWeight;\nvarying float vBiomeWinterBeachWeight;\nattribute vec2 biomeBeachUv;\nvarying vec2 vBiomeBeachUv;\nattribute vec4 biomeWeights;\nattribute float biomeNightWeight;\nvarying float vBiomeNightWeight;\nattribute float biomeCherryWeight;\nvarying float vBiomeCherryWeight;\nattribute vec2 biomeSnowUv;\nvarying vec2 vBiomeSnowUv;\nvarying vec4 vBiomeWeights;',
      )
      .replace(
        '#include <begin_vertex>',
        '#include <begin_vertex>\nvBiomeExtraA = biomeExtraA; vBiomeExtraB = biomeExtraB;\nvBiomeWinterBeachWeight = biomeWinterBeachWeight; vBiomeBeachUv = biomeBeachUv; vBiomeWeights = biomeWeights; vBiomeSnowUv = biomeSnowUv; vBiomeCherryWeight = biomeCherryWeight; vBiomeNightWeight = biomeNightWeight;',
      );
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        '#include <common>\nvarying vec4 vBiomeExtraA;\nvarying vec4 vBiomeExtraB;\nvarying float vBiomeNightWeight;\nvarying float vBiomeWinterBeachWeight;\nvarying vec2 vBiomeBeachUv;\nvarying float vBiomeCherryWeight;\nvarying vec4 vBiomeWeights;\nvarying vec2 vBiomeSnowUv;\nuniform sampler2D creativeAtlas;\nvec4 creativeSample(float layer, vec2 uv) { return texture2D(creativeAtlas, (vec2(mod(layer,4.0),3.0-floor(layer/4.0)) + clamp(uv,vec2(0.001),vec2(0.999))) / 4.0); }',
      )
      .replace(
        '#include <map_fragment>',
        `
#ifdef USE_MAP
vec4 terrainTexel = vec4(0.0);
if (vBiomeWeights.x > 0.0) terrainTexel += texture2D(map, vMapUv) * vBiomeWeights.x;
if (vBiomeWeights.y > 0.0) terrainTexel += creativeSample(0.0, vBiomeSnowUv) * vBiomeWeights.y;
if (vBiomeWeights.z > 0.0) terrainTexel += creativeSample(1.0, vMapUv) * vBiomeWeights.z;
if (vBiomeWeights.w > 0.0) terrainTexel += creativeSample(2.0, vMapUv) * vBiomeWeights.w;
if (vBiomeCherryWeight > 0.0) terrainTexel += creativeSample(3.0, vMapUv) * vBiomeCherryWeight;
if (vBiomeNightWeight > 0.0) terrainTexel += creativeSample(4.0, vMapUv) * vBiomeNightWeight;
if (vBiomeWinterBeachWeight > 0.0) terrainTexel += creativeSample(0.0, vBiomeBeachUv) * vBiomeWinterBeachWeight;
if (vBiomeExtraA.x > 0.0) terrainTexel += creativeSample(5.0, vMapUv) * vBiomeExtraA.x;
if (vBiomeExtraA.y > 0.0) terrainTexel += creativeSample(6.0, vMapUv) * vBiomeExtraA.y;
if (vBiomeExtraA.z > 0.0) terrainTexel += creativeSample(7.0, vMapUv) * vBiomeExtraA.z;
if (vBiomeExtraA.w > 0.0) terrainTexel += creativeSample(8.0, vMapUv) * vBiomeExtraA.w;
if (vBiomeExtraB.x > 0.0) terrainTexel += creativeSample(9.0, vMapUv) * vBiomeExtraB.x;
if (vBiomeExtraB.y > 0.0) terrainTexel += creativeSample(10.0, vMapUv) * vBiomeExtraB.y;
if (vBiomeExtraB.z > 0.0) terrainTexel += creativeSample(11.0, vMapUv) * vBiomeExtraB.z;
if (vBiomeExtraB.w > 0.0) terrainTexel += creativeSample(12.0, vMapUv) * vBiomeExtraB.w;
diffuseColor *= terrainTexel;
#endif`,
      );
  };
  material.customProgramCacheKey = () => `${legacyKey}:creative-biomes-v11`;
  material.needsUpdate = true;
}
